// ---------------------------------------------------------------------------
// Analyst module — Review Queue page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { admin } from '@noxverify/api-contracts';
import { api } from '../lib/api';
import { useProjectContext } from '../lib/project-context';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { useFormatters } from '../hooks/useFormatters';

export function ReviewQueuePage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const taskTypeLabels = t('reviewQueue.taskTypes', { returnObjects: true }) as Record<string, string>;
  const statusOptions = t('reviewQueue.filters.statusOptions', { returnObjects: true }) as Record<string, string>;
  const priorityOptions = t('reviewQueue.filters.priorityOptions', { returnObjects: true }) as Record<string, string>;
  const columns = useMemo<Column<admin.ReviewTaskSummary>[]>(
    () => [
      {
        key: 'caseId',
        header: t('reviewQueue.columns.caseId'),
        render: (task) => (
          <span className="text-sm font-mono text-ink">{task.caseId}</span>
        ),
      },
      {
        key: 'taskType',
        header: t('reviewQueue.columns.taskType'),
        render: (task) => (
          <span className="text-sm text-ink">
            {taskTypeLabels[task.taskType] ?? task.taskType}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('reviewQueue.columns.status'),
        render: (task) => <StatusBadge value={task.status} />,
      },
      {
        key: 'priority',
        header: t('reviewQueue.columns.priority'),
        render: (task) => <StatusBadge value={task.priority} variant="priority" />,
      },
      {
        key: 'dueAt',
        header: t('reviewQueue.columns.due'),
        render: (task) => (
          <span className="text-sm text-ink-muted">
            {task.dueAt ? f.date(task.dueAt, { dateStyle: 'medium' }) : '--'}
          </span>
        ),
      },
      {
        key: 'createdAt',
        header: t('reviewQueue.columns.created'),
        render: (task) => (
          <span className="text-sm text-ink-muted tabular-nums font-mono">
            {f.date(task.createdAt, { dateStyle: 'medium' })}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        render: (task) =>
          task.status === 'open' ? (
            <button
              type="button"
              className="px-3 py-1 text-xs font-medium bg-info-tint text-info rounded hover:bg-info-tint focus:outline-none focus:ring-2 focus:ring-primary/25 focus:ring-offset-1 transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                // ASSUMPTION: Assign-to-me triggers an assignment API call.
                // The actual member ID would come from auth context.
              }}
            >
              {t('reviewQueue.actions.assignToMe')}
            </button>
          ) : null,
      },
    ],
    [f, t, taskTypeLabels],
  );
  const { projectId } = useProjectContext();
  const [tasks, setTasks] = useState<admin.ReviewTaskSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');

  const loadTasks = useCallback(() => {
    if (!projectId) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const params: Omit<admin.ListReviewTasksParams, 'projectId'> = {};
    if (statusFilter) {
      params.status = statusFilter as admin.ReviewTaskStatus;
    }
    if (priorityFilter) {
      params.priority = priorityFilter as admin.ReviewTaskPriority;
    }

    api.admin
      .listReviewTasks(projectId, params)
      .then((res) => setTasks(res.data))
      .catch((err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : typeof err === 'object' && err !== null && 'message' in err
              ? String((err as { message: unknown }).message)
              : t('reviewQueue.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [projectId, statusFilter, priorityFilter, t]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  if (!projectId) {
    return (
      <div className="p-6">
        <h1 className="font-display text-3xl text-ink mb-4">{t('reviewQueue.headings.list')}</h1>
        <div className="bg-primary-tint border border-primary/30 rounded-sm p-4 text-primary">
          <p className="text-sm">
            {t('reviewQueue.messages.selectProject')}
          </p>
        </div>
      </div>
    );
  }

  if (loading) return <LoadingState rows={5} />;
  if (error) return <ErrorState message={error} onRetry={loadTasks} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('reviewQueue.headings.list')}</h1>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <div>
          <label
            htmlFor="status-filter"
            className="block text-xs font-medium text-ink-muted mb-1"
          >
            {t('reviewQueue.filters.statusLabel')}
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-40 rounded-sm border border-hairline-strong px-3 py-1.5 text-sm text-ink focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">{t('common.all')}</option>
            <option value="open">{statusOptions.open}</option>
            <option value="assigned">{statusOptions.assigned}</option>
            <option value="in_progress">{statusOptions.in_progress}</option>
            <option value="completed">{statusOptions.completed}</option>
            <option value="cancelled">{statusOptions.cancelled}</option>
            <option value="expired">{statusOptions.expired}</option>
          </select>
        </div>
        <div>
          <label
            htmlFor="priority-filter"
            className="block text-xs font-medium text-ink-muted mb-1"
          >
            {t('reviewQueue.filters.priorityLabel')}
          </label>
          <select
            id="priority-filter"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="block w-40 rounded-sm border border-hairline-strong px-3 py-1.5 text-sm text-ink focus:border-primary focus:ring-1 focus:ring-primary"
          >
            <option value="">{t('common.all')}</option>
            <option value="critical">{priorityOptions.critical}</option>
            <option value="high">{priorityOptions.high}</option>
            <option value="medium">{priorityOptions.medium}</option>
            <option value="low">{priorityOptions.low}</option>
          </select>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={tasks}
        rowKey={(task) => task.id}
        emptyMessage={t('reviewQueue.empty')}
        ariaLabel={t('reviewQueue.ariaLabel')}
      />
    </div>
  );
}
